import type { ArticleBlock } from "@/lib/types/content";

function normalizeText(text: string): string {
  return text.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
}

function isHeadingClass(className: string): "h2" | "h3" | null {
  const lower = className.toLowerCase();
  if (/msoheading1|heading1|heading-1|title/.test(lower)) return "h2";
  if (/msoheading2|heading2|heading-2/.test(lower)) return "h2";
  if (/msoheading3|heading3|heading-3/.test(lower)) return "h3";
  return null;
}

function outlineLevelFromStyle(style: string): "h2" | "h3" | null {
  const match = /mso-outline-level:\s*(\d+)/i.exec(style);
  if (!match) return null;
  const level = Number(match[1]);
  if (level <= 2) return "h2";
  if (level === 3) return "h3";
  return null;
}

function inferHeadingFromPlainLine(line: string): "h2" | "h3" | null {
  const text = normalizeText(line);
  if (!text || text.length > 120) return null;
  if (/^\d+\.\s+\S/.test(text)) return "h2";
  if (text.endsWith(".") || text.endsWith("?") || text.endsWith("!")) return null;
  if (text.split(" ").length <= 12) return "h2";
  return null;
}

function pushParagraph(blocks: ArticleBlock[], text: string, variant: "p" | "h2" | "h3" = "p") {
  const normalized = normalizeText(text);
  if (!normalized) return;
  if (variant === "p") {
    blocks.push({ type: "p", text: normalized });
  } else {
    blocks.push({ type: variant, text: normalized });
  }
}

function pushList(blocks: ArticleBlock[], items: string[]) {
  const cleaned = items.map(normalizeText).filter(Boolean);
  if (cleaned.length) blocks.push({ type: "ul", items: cleaned });
}

function walkElements(node: Node, blocks: ArticleBlock[]) {
  if (node.nodeType === Node.TEXT_NODE) return;

  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (tag === "h1" || tag === "h2") {
    pushParagraph(blocks, el.textContent ?? "", "h2");
    return;
  }
  if (tag === "h3" || tag === "h4" || tag === "h5" || tag === "h6") {
    pushParagraph(blocks, el.textContent ?? "", "h3");
    return;
  }
  if (tag === "ul" || tag === "ol") {
    const items = Array.from(el.querySelectorAll(":scope > li")).map((li) => li.textContent ?? "");
    pushList(blocks, items);
    return;
  }
  if (tag === "p") {
    const className = el.getAttribute("class") ?? "";
    const style = el.getAttribute("style") ?? "";
    const heading =
      isHeadingClass(className) ?? outlineLevelFromStyle(style) ?? inferHeadingFromPlainLine(el.textContent ?? "");
    pushParagraph(blocks, el.textContent ?? "", heading ?? "p");
    return;
  }
  if (tag === "div" || tag === "article" || tag === "section" || tag === "body") {
    for (const child of Array.from(el.childNodes)) {
      walkElements(child, blocks);
    }
    return;
  }
  if (tag === "br") return;
  if (el.textContent?.trim()) {
    pushParagraph(blocks, el.textContent, "p");
  }
}

export function parseHtmlToArticleBlocks(html: string): ArticleBlock[] {
  const trimmed = html.trim();
  if (!trimmed) return [];

  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(trimmed, "text/html");
    const blocks: ArticleBlock[] = [];
    walkElements(doc.body, blocks);
    if (blocks.length) return blocks;
  }

  return parsePlainTextToArticleBlocks(
    trimmed.replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n\n").replace(/<[^>]+>/g, ""),
  );
}

export function parsePlainTextToArticleBlocks(text: string): ArticleBlock[] {
  const blocks: ArticleBlock[] = [];
  const paragraphs = text
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  for (const paragraph of paragraphs) {
    const lines = paragraph.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) continue;

    const bulletLines = lines.filter((l) => /^[-•*]\s+/.test(l));
    if (bulletLines.length === lines.length) {
      pushList(
        blocks,
        lines.map((l) => l.replace(/^[-•*]\s+/, "")),
      );
      continue;
    }

    const combined = lines.join(" ");
    const heading = inferHeadingFromPlainLine(combined);
    pushParagraph(blocks, combined, heading ?? "p");
  }

  return blocks;
}

export function parseClipboardToArticleBlocks(html: string | null | undefined, plain: string | null | undefined): ArticleBlock[] {
  if (html?.trim()) {
    const fromHtml = parseHtmlToArticleBlocks(html);
    if (fromHtml.length > 1) return fromHtml;
    if (fromHtml.length === 1 && !plain?.includes("\n\n")) return fromHtml;
  }
  if (plain?.trim()) {
    return parsePlainTextToArticleBlocks(plain);
  }
  return html?.trim() ? parseHtmlToArticleBlocks(html) : [];
}
